import { App, Button, Dropdown } from 'antd'
import { GlobalOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { SUPPORTED_LANGUAGES } from '@/i18n'
import styled from 'styled-components'
import { configureLangHeader } from '@/libs/services'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getStaleTime, QUERY_TYPES } from '@/config/reactQuery'
import SettingsService from '@/libs/services/settings'
import { useEffect, useCallback } from 'react'

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LanguageSwitcher = ({ anonymous = false }) => {
  const { i18n, t } = useTranslation()
  const { notification } = App.useApp()
  const queryClient = useQueryClient()

  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => SettingsService.getMe(),
    staleTime: getStaleTime(QUERY_TYPES.USER),
    enabled: !anonymous,
  })

  const userLanguage = userData?.data?.language

  // Mutation to update user language in the backend
  const updateLanguageMutation = useMutation({
    mutationFn: language => SettingsService.updateMe({ language }),
    onSuccess: data => {
      const newLanguage = data.data.language
      i18n.changeLanguage(newLanguage)
      configureLangHeader(newLanguage)
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: ['me'], exact: true })

      notification.success({
        title: i18n.t('common.languageUpdated', { lng: newLanguage }),
        description: i18n.t('common.languageUpdatedDescription', { lng: newLanguage }),
        placement: 'topRight',
        duration: 3,
      })
    },
    onError: error => {
      console.error('Error updating language:', error)

      // Revert to previous language on error
      const currentLanguage = userLanguage || i18n.language
      i18n.changeLanguage(currentLanguage)
      configureLangHeader(currentLanguage)

      notification.error({
        title: t('common.languageUpdateError'),
        description: t('common.languageUpdateErrorDescription'),
        placement: 'topRight',
        duration: 4,
      })
    },
  })

  // Validate and set user language when available (only when user data or loading state changes)
  useEffect(() => {
    if (userLoading) return // Do nothing while loading

    const currentLanguage = (i18n.language || '').split('-')[0]
    const targetLanguage = (userLanguage || currentLanguage || '').split('-')[0]

    // Ensure the language is supported and only change when actually different
    if (SUPPORTED_LANGUAGES.includes(targetLanguage) && currentLanguage !== targetLanguage) {
      i18n.changeLanguage(targetLanguage)
      configureLangHeader(targetLanguage)
    }
    // i18n intentionally omitted from deps to avoid re-run loop when language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLanguage, userLoading])

  const handleLanguageChange = useCallback(
    async lang => {
      // Ensure the language is supported
      if (!SUPPORTED_LANGUAGES.includes(lang)) {
        notification.error({
          title: t('common.unsupportedLanguage'),
          placement: 'topRight',
          duration: 3,
        })
        return
      }

      // Change i18n language and wait for it to load so the UI updates
      await i18n.changeLanguage(lang)
      configureLangHeader(lang)

      // If user is logged in and language differs, update in the backend
      if (userData?.data && userLanguage !== lang) {
        updateLanguageMutation.mutate(lang)
      }
    },
    [i18n, userData, userLanguage, updateLanguageMutation, t, notification]
  )

  // Get current language for display
  const getCurrentLanguage = () => {
    if (anonymous) return i18n.language?.toUpperCase()
    if (userLoading) return i18n.language?.toUpperCase()
    return (userLanguage || i18n.language)?.toUpperCase()
  }

  const items = SUPPORTED_LANGUAGES.map(lang => ({
    key: lang,
    label: lang.toUpperCase(),
    onClick: () => handleLanguageChange(lang),
  }))

  return (
    <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
      <StyledButton
        type="text"
        icon={<GlobalOutlined />}
        loading={!anonymous && (updateLanguageMutation.isPending || userLoading)}
      >
        {getCurrentLanguage()}
      </StyledButton>
    </Dropdown>
  )
}

LanguageSwitcher.propTypes = {
  anonymous: PropTypes.bool,
}

export default LanguageSwitcher
